import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, GripVertical, RotateCcw, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { dayLabel } from './routeDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const StopItem = ({ s, i, last, move, dragProps }) => (
  <div {...dragProps}
       className="flex items-center gap-2 bg-white border border-zinc-200 rounded-lg px-2 py-2 cursor-grab active:cursor-grabbing"
       data-testid={`route-order-item-${i}`}>
    <GripVertical className="w-4 h-4 text-zinc-300 shrink-0 hidden sm:block" />
    <div className="w-7 h-7 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-zinc-900 truncate">{s.company}</div>
      <div className="text-[11px] text-zinc-500 truncate">{s.address || 'Adres ontbreekt'} · {s.motor_count} motor(en)</div>
    </div>
    <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 rounded-md hover:bg-zinc-100 disabled:opacity-30" data-testid={`route-order-up-${i}`}>
      <ArrowUp className="w-4 h-4" />
    </button>
    <button onClick={() => move(i, 1)} disabled={last} className="p-2 rounded-md hover:bg-zinc-100 disabled:opacity-30" data-testid={`route-order-down-${i}`}>
      <ArrowDown className="w-4 h-4" />
    </button>
  </div>
);

// Handmatige stopvolgorde per chauffeur (slepen of pijltjes).
export const RouteOrderDialog = ({ driver, date = '', onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [stops, setStops] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!driver) return;
    setData(null);
    axios.get(`${API}/admin/chauffeur-route-order/${driver}`, { params: { date }, headers: auth() })
      .then((r) => { setData(r.data); setStops(r.data.stops); })
      .catch(() => toast.error('Route laden mislukt'));
  }, [driver, date]);

  const move = (i, dir) => setStops((ss) => {
    const n = [...ss];
    const j = i + dir;
    if (j < 0 || j >= n.length) return ss;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });

  const dropOn = (target) => {
    if (dragIdx === null || dragIdx === target) return;
    setStops((ss) => {
      const n = [...ss];
      const [item] = n.splice(dragIdx, 1);
      n.splice(target, 0, item);
      return n;
    });
    setDragIdx(null);
  };

  const save = async (stopIds) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/chauffeur-route-order/${driver}`, { stop_ids: stopIds }, { params: { date }, headers: auth() });
      toast.success(stopIds ? `Volgorde opgeslagen voor ${driver} (~${r.data.estimated_total_km} km)` : `${driver}: weer automatische volgorde`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!driver} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="route-order-dialog">
        <DialogHeader>
          <DialogTitle>Volgorde aanpassen: {driver} · {dayLabel(data?.date || date)}</DialogTitle>
          <DialogDescription>
            Start: Schalkhaar → stops → eind: Coppet. {data?.manual_order ? 'Nu: handmatige volgorde.' : 'Nu: automatisch berekend.'}
          </DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
        ) : stops.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center">Geen openstaande stops voor {driver}.</p>
        ) : (
          <div className="space-y-1.5 max-h-[55vh] overflow-auto">
            {stops.map((s, i) => (
              <StopItem key={s.stop_id} s={s} i={i} last={i === stops.length - 1} move={move}
                        dragProps={{
                          draggable: true,
                          onDragStart: () => setDragIdx(i),
                          onDragOver: (e) => e.preventDefault(),
                          onDrop: () => dropOn(i),
                        }} />
            ))}
          </div>
        )}
        <DialogFooter className="gap-2 sm:justify-between">
          <button onClick={() => save(null)} disabled={busy || !data?.manual_order}
                  className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-zinc-300 disabled:opacity-40" data-testid="route-order-auto">
            <RotateCcw className="w-4 h-4" /> Automatisch
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-zinc-300" data-testid="route-order-cancel">Annuleren</button>
            <button onClick={() => save(stops.map((s) => s.stop_id))} disabled={busy || stops.length === 0}
                    className="px-4 py-2 text-sm rounded-lg bg-purple-600 text-white font-semibold disabled:opacity-40" data-testid="route-order-save">
              {busy ? 'Opslaan…' : 'Opslaan'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
